import { useState, useEffect } from 'react';
import { RefreshCw, Plus, Check, X, Trash2, Undo2, Clock, CheckSquare, Square } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import {
    fetchUsers,
    updateUserCredits,
    deleteUser,
    cancelUserDeletion,
    User,
} from '../../../lib/prompt-admin-api';

export function UsersTab() {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [editingUser, setEditingUser] = useState<string | null>(null);
    const [creditsInput, setCreditsInput] = useState('');

    // Bulk selection state
    const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

    // Delete dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [deleteImmediate, setDeleteImmediate] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Bulk delete dialog state
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [bulkDeleteImmediate, setBulkDeleteImmediate] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

    useEffect(() => {
        loadUsers();
    }, [page]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const data = await fetchUsers(page, 20);
            setUsers(data.users);
            setTotalPages(data.pagination.totalPages);
        } catch (error) {
            toast.error('Nie udało się załadować użytkowników');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCredits = async (userId: string) => {
        const credits = parseInt(creditsInput);
        if (isNaN(credits) || credits <= 0) {
            toast.error('Podaj prawidłową liczbę kredytów');
            return;
        }
        try {
            await updateUserCredits(userId, credits, 'add');
            toast.success(`Dodano ${credits} kredytów`);
            setEditingUser(null);
            setCreditsInput('');
            loadUsers();
        } catch (error) {
            toast.error('Nie udało się dodać kredytów');
        }
    };

    const handleDeleteClick = (user: User, immediate: boolean) => {
        setUserToDelete(user);
        setDeleteImmediate(immediate);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;

        setDeleteLoading(true);
        try {
            await deleteUser(userToDelete.id, deleteImmediate);
            toast.success(
                deleteImmediate
                    ? `Użytkownik ${userToDelete.email} został usunięty`
                    : `Użytkownik ${userToDelete.email} zaplanowany do usunięcia (30 dni)`
            );
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            loadUsers();
        } catch (error) {
            toast.error('Nie udało się usunąć użytkownika');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCancelDeletion = async (user: User) => {
        try {
            await cancelUserDeletion(user.id);
            toast.success(`Anulowano usunięcie konta ${user.email}`);
            loadUsers();
        } catch (error) {
            toast.error('Nie udało się anulować usunięcia');
        }
    };

    // Bulk selection handlers
    const toggleUserSelection = (userId: string) => {
        setSelectedUsers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    const toggleAllUsers = () => {
        if (selectedUsers.size === users.length) {
            setSelectedUsers(new Set());
        } else {
            setSelectedUsers(new Set(users.map(u => u.id)));
        }
    };

    const clearSelection = () => {
        setSelectedUsers(new Set());
    };

    // Bulk delete handler
    const handleBulkDeleteClick = (immediate: boolean) => {
        setBulkDeleteImmediate(immediate);
        setBulkDeleteDialogOpen(true);
    };

    const handleBulkDeleteConfirm = async () => {
        setBulkDeleteLoading(true);
        const selectedUsersList = users.filter(u => selectedUsers.has(u.id));
        let successCount = 0;
        let errorCount = 0;

        try {
            for (const user of selectedUsersList) {
                try {
                    await deleteUser(user.id, bulkDeleteImmediate);
                    successCount++;
                } catch {
                    errorCount++;
                }
            }

            if (successCount > 0) {
                toast.success(
                    bulkDeleteImmediate
                        ? `Usunięto ${successCount} użytkowników`
                        : `Zaplanowano usunięcie ${successCount} użytkowników (30 dni)`
                );
            }
            if (errorCount > 0) {
                toast.error(`Nie udało się usunąć ${errorCount} użytkowników`);
            }

            setBulkDeleteDialogOpen(false);
            clearSelection();
            loadUsers();
        } finally {
            setBulkDeleteLoading(false);
        }
    };

    // Bulk cancel deletion handler
    const handleBulkCancelDeletion = async () => {
        const selectedUsersList = users.filter(u => selectedUsers.has(u.id) && u.scheduled_deletion_at);
        if (selectedUsersList.length === 0) {
            toast.error('Brak użytkowników zaplanowanych do usunięcia w zaznaczeniu');
            return;
        }

        let successCount = 0;
        let errorCount = 0;

        for (const user of selectedUsersList) {
            try {
                await cancelUserDeletion(user.id);
                successCount++;
            } catch {
                errorCount++;
            }
        }

        if (successCount > 0) {
            toast.success(`Anulowano usunięcie ${successCount} kont`);
        }
        if (errorCount > 0) {
            toast.error(`Nie udało się anulować ${errorCount} kont`);
        }

        clearSelection();
        loadUsers();
    };

    if (loading) {
        return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Użytkownicy ({users.length})</h2>
                <Button variant="outline" size="sm" onClick={loadUsers}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Odśwież
                </Button>
            </div>

            {/* Bulk Actions Toolbar */}
            {selectedUsers.size > 0 && (
                <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">
                                Zaznaczono: {selectedUsers.size} {selectedUsers.size === 1 ? 'użytkownik' : 'użytkowników'}
                            </span>
                            <Button variant="ghost" size="sm" onClick={clearSelection}>
                                <X className="w-4 h-4 mr-1" />
                                Wyczyść
                            </Button>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleBulkDeleteClick(false)}
                                className="text-red-600 border-red-600/50 hover:bg-red-50"
                            >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Soft delete (30 dni)
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleBulkDeleteClick(true)}
                                className="text-red-600 border-red-600/50 hover:bg-red-50"
                            >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Usuń natychmiast
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleBulkCancelDeletion}
                                className="text-green-600 border-green-600/50 hover:bg-green-50"
                            >
                                <Undo2 className="w-4 h-4 mr-1" />
                                Anuluj usunięcie
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full">
                    <thead className="bg-muted/50">
                        <tr>
                            <th className="text-center p-3 text-sm font-medium w-12">
                                <button
                                    onClick={toggleAllUsers}
                                    className="hover:bg-muted/50 rounded p-1 transition-colors"
                                >
                                    {selectedUsers.size === users.length && users.length > 0 ? (
                                        <CheckSquare className="w-5 h-5" />
                                    ) : (
                                        <Square className="w-5 h-5" />
                                    )}
                                </button>
                            </th>
                            <th className="text-left p-3 text-sm font-medium">Email</th>
                            <th className="text-left p-3 text-sm font-medium">Imię</th>
                            <th className="text-center p-3 text-sm font-medium">Kredyty</th>
                            <th className="text-center p-3 text-sm font-medium">Status</th>
                            <th className="text-right p-3 text-sm font-medium">Akcje</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => {
                            const isScheduledForDeletion = !!user.scheduled_deletion_at;
                            const isSelected = selectedUsers.has(user.id);

                            return (
                                <tr key={user.id} className={`border-t border-border ${isScheduledForDeletion ? 'bg-red-50/50 dark:bg-red-900/10' : ''} ${isSelected ? 'bg-primary/5' : ''}`}>
                                    <td className="p-3 text-center">
                                        <button
                                            onClick={() => toggleUserSelection(user.id)}
                                            className="hover:bg-muted/50 rounded p-1 transition-colors"
                                        >
                                            {isSelected ? (
                                                <CheckSquare className="w-5 h-5 text-primary" />
                                            ) : (
                                                <Square className="w-5 h-5" />
                                            )}
                                        </button>
                                    </td>
                                    <td className="p-3 text-sm">
                                        <div className="flex items-center gap-2">
                                            {user.email}
                                            {isScheduledForDeletion && (
                                                <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                                                    <Clock className="w-3 h-3" />
                                                    Do usunięcia
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-3 text-sm text-muted-foreground">{user.full_name || '-'}</td>
                                    <td className="p-3 text-sm text-center font-medium">{user.available_credits}</td>
                                    <td className="p-3 text-sm text-center">
                                        <span className={`px-2 py-0.5 rounded text-xs ${user.subscription_status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-muted text-muted-foreground'
                                            }`}>
                                            {user.credits_type || 'free'}
                                        </span>
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            {editingUser === user.id ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        value={creditsInput}
                                                        onChange={(e) => setCreditsInput(e.target.value)}
                                                        placeholder="Ilość"
                                                        className="w-20 px-2 py-1 text-sm border border-border rounded"
                                                    />
                                                    <Button size="sm" variant="ghost" onClick={() => handleAddCredits(user.id)}>
                                                        <Check className="w-4 h-4" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => setEditingUser(null)}>
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button size="sm" variant="outline" onClick={() => { setEditingUser(user.id); setCreditsInput(''); }}>
                                                        <Plus className="w-4 h-4 mr-1" />
                                                        Kredyty
                                                    </Button>
                                                    {isScheduledForDeletion ? (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="text-green-600 border-green-600/50 hover:bg-green-50"
                                                            onClick={() => handleCancelDeletion(user)}
                                                        >
                                                            <Undo2 className="w-4 h-4 mr-1" />
                                                            Przywróć
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="text-red-600 border-red-600/50 hover:bg-red-50"
                                                            onClick={() => handleDeleteClick(user, false)}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center gap-2">
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                        Poprzednia
                    </Button>
                    <span className="px-4 py-2 text-sm">Strona {page} z {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                        Następna
                    </Button>
                </div>
            )}

            {/* Delete User Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-red-600">
                            Usuń użytkownika
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                            <p>
                                Czy na pewno chcesz usunąć użytkownika <strong>{userToDelete?.email}</strong>?
                            </p>
                            <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="deleteType"
                                        checked={!deleteImmediate}
                                        onChange={() => setDeleteImmediate(false)}
                                        className="w-4 h-4"
                                    />
                                    <div>
                                        <p className="font-medium text-sm">Soft delete (30 dni)</p>
                                        <p className="text-xs text-muted-foreground">
                                            Konto zostanie zaplanowane do usunięcia za 30 dni
                                        </p>
                                    </div>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="deleteType"
                                        checked={deleteImmediate}
                                        onChange={() => setDeleteImmediate(true)}
                                        className="w-4 h-4"
                                    />
                                    <div>
                                        <p className="font-medium text-sm text-red-600">Natychmiastowe usunięcie</p>
                                        <p className="text-xs text-muted-foreground">
                                            Konto i wszystkie dane zostaną usunięte natychmiast
                                        </p>
                                    </div>
                                </label>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteLoading}>Anuluj</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            disabled={deleteLoading}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deleteLoading ? 'Usuwanie...' : deleteImmediate ? 'Usuń natychmiast' : 'Zaplanuj usunięcie'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Bulk Delete Dialog */}
            <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-red-600">
                            Usuń użytkowników
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                            <p>
                                Czy na pewno chcesz {bulkDeleteImmediate ? 'natychmiast usunąć' : 'zaplanować usunięcie'} <strong>{selectedUsers.size}</strong> {selectedUsers.size === 1 ? 'użytkownika' : 'użytkowników'}?
                            </p>
                            <div className="bg-muted/50 rounded-lg p-3 max-h-32 overflow-y-auto">
                                {users.filter(u => selectedUsers.has(u.id)).map(u => (
                                    <div key={u.id} className="text-xs text-muted-foreground py-1">
                                        • {u.email}
                                    </div>
                                ))}
                            </div>
                            <p className="text-sm font-medium">
                                {bulkDeleteImmediate
                                    ? '⚠️ Konta i wszystkie dane zostaną usunięte natychmiast i nieodwracalnie.'
                                    : 'Konta zostaną zaplanowane do usunięcia za 30 dni. Możesz anulować to działanie przed upływem tego czasu.'}
                            </p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={bulkDeleteLoading}>Anuluj</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleBulkDeleteConfirm}
                            disabled={bulkDeleteLoading}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {bulkDeleteLoading ? 'Usuwanie...' : bulkDeleteImmediate ? 'Usuń natychmiast' : 'Zaplanuj usunięcie'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
